/**
 * Studio Booking · Media upload components — extracted Oct 2026.
 * Contents:
 *   • MediaGallery       — multi-media (image + video + caption) uploader
 *   • StudioGalleryEditor — up to 12 photos for the public widget hero slideshow
 *   • GuidelinesUpload   — PDF/doc upload with preview + remove
 *   • ImageUpload        — single-image uploader with optional crop/zoom
 *   • ZoomPanDialog      — zoom + pan + export cropped image to blob
 *   • CropDialog         — fixed-aspect crop (react-image-crop) + export
 *   • MediaBadge         — read-side 10×7 thumbnail chip for list rows
 *
 * All components call the workspace-scoped `/studio/upload-image?purpose=…`
 * endpoint; the server stores files in Emergent Object Storage and returns
 * `{file_id, url, kind}`. No cross-component state is shared with the
 * rest of the Studio hub, so this file is a safe leaf extraction.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ReactCrop from "react-image-crop";
import { Plus, Trash2, GripVertical, X as XIcon, Image as ImageIcon } from "lucide-react";

// Feb 2026 · Inline minimal styles for the ReactCrop overlay instead of
// importing the package's CSS — the CSS import was pathologically slow
// in dev-server compiles (~15min hang). This block covers just what
// the widget needs; the lib works fine without the stylesheet.
const _CROP_INLINE_CSS = `
.ReactCrop { position: relative; display: inline-block; cursor: crosshair; overflow: hidden; max-width: 100%; }
.ReactCrop__crop-selection { position: absolute; top: 0; left: 0; box-sizing: border-box; cursor: move; box-shadow: 0 0 0 9999em rgba(0, 0, 0, .5); border: 1px dashed #fff; touch-action: manipulation; }
.ReactCrop__drag-handle { position: absolute; width: 12px; height: 12px; background: #fff; border: 1px solid rgba(0,0,0,.5); box-sizing: border-box; }
.ReactCrop__drag-handle.ord-nw { top: -6px; left: -6px; cursor: nwse-resize; }
.ReactCrop__drag-handle.ord-n  { top: -6px; left: 50%; margin-left: -6px; cursor: ns-resize; }
.ReactCrop__drag-handle.ord-ne { top: -6px; right: -6px; cursor: nesw-resize; }
.ReactCrop__drag-handle.ord-e  { top: 50%; right: -6px; margin-top: -6px; cursor: ew-resize; }
.ReactCrop__drag-handle.ord-se { bottom: -6px; right: -6px; cursor: nwse-resize; }
.ReactCrop__drag-handle.ord-s  { bottom: -6px; left: 50%; margin-left: -6px; cursor: ns-resize; }
.ReactCrop__drag-handle.ord-sw { bottom: -6px; left: -6px; cursor: nesw-resize; }
.ReactCrop__drag-handle.ord-w  { top: 50%; left: -6px; margin-top: -6px; cursor: ew-resize; }
.ReactCrop__child-wrapper { max-height: inherit; overflow: hidden; }
`;

// Feb 2026 · Multi-media gallery — photos + videos + captions.
// Value is an array of {id, url, kind: image|video, caption, order}.
// Uses the studio upload endpoint with purpose=gallery (accepts videos
// up to 50MB per file).
export function MediaGallery({ ws, value, onChange, purpose = "gallery", testid, disabled }) {
  const items = Array.isArray(value) ? [...value].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ i: 0, n: 0 });
  const inputRef = useRef(null);
  const _abs = (u) => (u && u.startsWith("/api/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true); setProgress({ i: 0, n: list.length });
    const next = [...items];
    for (let idx = 0; idx < list.length; idx++) {
      const f = list[idx];
      setProgress({ i: idx + 1, n: list.length });
      if (!/^(image|video)\//i.test(f.type)) {
        toast.error(`Skipped ${f.name} — unsupported type`);
        continue;
      }
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post(
          `/workspaces/${ws}/studio/upload-image?purpose=${encodeURIComponent(purpose)}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push({
          id: data.file_id, url: data.url, kind: data.kind || (f.type.startsWith("video/") ? "video" : "image"),
          caption: "", order: next.length,
        });
        onChange && onChange(next.slice());
      } catch (e) { toast.error(apiErrorMessage(e, `Upload failed for ${f.name}`)); }
    }
    setBusy(false); setProgress({ i: 0, n: 0 });
  };
  const removeAt = (i) => {
    const next = items.filter((_, j) => j !== i).map((m, idx) => ({ ...m, order: idx }));
    onChange && onChange(next);
  };
  const setCaption = (i, cap) => {
    const next = items.map((m, j) => j === i ? { ...m, caption: cap } : m);
    onChange && onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange && onChange(next.map((m, idx) => ({ ...m, order: idx })));
  };
  const onDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    upload(e.dataTransfer?.files);
  };
  return (
    <div data-testid={testid}>
      <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
            className={`border-2 border-dashed border-border ${disabled ? "opacity-60" : ""} p-3`}>
        {items.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-xs text-muted-foreground italic mb-2">No photos or videos yet</div>
            <Button size="sm" type="button" onClick={() => inputRef.current?.click()}
                     disabled={disabled || busy} className="rounded-none h-8"
                     data-testid={`${testid}-add-first`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add media
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {items.map((m, i) => (
                <div key={m.id || i} className="border border-border relative group bg-white" data-testid={`${testid}-item-${i}`}>
                  <div className="aspect-video bg-neutral-100 flex items-center justify-center overflow-hidden">
                    {m.kind === "video" ? (
                      <video src={_abs(m.url)} preload="metadata" controls={false} muted playsInline
                              className="w-full h-full object-cover" />
                    ) : (
                      <img src={_abs(m.url)} alt={m.caption || ""} loading="lazy"
                            className="w-full h-full object-cover" />
                    )}
                    {m.kind === "video" && (
                      <span className="absolute top-1 left-1 text-[9px] bg-black/70 text-white px-1.5 py-0.5 uppercase tracking-widest">▶ Video</span>
                    )}
                  </div>
                  <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => move(i, -1)} disabled={disabled || i === 0}
                            className="bg-white/95 hover:bg-primary hover:text-white p-1 border border-border disabled:opacity-40"
                            title="Move left"><GripVertical className="w-3 h-3 rotate-90" /></button>
                    <button type="button" onClick={() => removeAt(i)} disabled={disabled}
                            className="bg-white/95 hover:bg-rose-500 hover:text-white text-rose-600 p-1 border border-border"
                            title="Remove"><XIcon className="w-3 h-3" /></button>
                  </div>
                  <input value={m.caption || ""}
                          onChange={(e) => setCaption(i, e.target.value)}
                          placeholder="Caption…" disabled={disabled}
                          className="w-full text-[10px] border-t border-border px-1.5 py-1 focus:outline-none focus:bg-primary/5"
                          data-testid={`${testid}-caption-${i}`} />
                </div>
              ))}
              <button type="button" onClick={() => inputRef.current?.click()}
                       disabled={disabled || busy}
                       className={`border-2 border-dashed border-border aspect-video flex flex-col items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors ${disabled ? "opacity-40" : ""}`}
                       data-testid={`${testid}-add-more`}>
                <Plus className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground mt-1">Add more</span>
              </button>
            </div>
          </>
        )}
        <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden"
                disabled={disabled || busy}
                onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                data-testid={`${testid}-input`} />
        {busy && (
          <div className="text-[10px] text-primary mt-2" data-testid={`${testid}-progress`}>
            Uploading {progress.i} of {progress.n}…
          </div>
        )}
        <div className="text-[10px] text-muted-foreground mt-2">
          Drop images (JPG/PNG/WebP/GIF, ≤8 MB) or videos (MP4/MOV/WebM, ≤50 MB) here.
          Drag the ↔ handle to reorder; captions save with the record.
        </div>
      </div>
    </div>
  );
}


/* Feb 2026 · R268kkk · Studio photo gallery editor. Up to 12
   free-aspect photos uploaded via `ImageUpload`; drag-and-drop reorder
   via up/down buttons, X to remove. Free aspect on upload — we display
   with `object-fit: cover` on the widget so orientation doesn't
   matter. */
export const STUDIO_GALLERY_MAX = 12;
export function StudioGalleryEditor({ ws, value, onChange }) {
  const [uploaderKey, setUploaderKey] = useState(0);
  const list = Array.isArray(value) ? value : [];
  const _abs = (u) => (u && u.startsWith("/api/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  const add = (url) => {
    if (!url) return;
    onChange([...list, url]);
    setUploaderKey((k) => k + 1); // reset uploader for the next photo
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    onChange(copy);
  };
  return (
    <div className="space-y-2" data-testid="sb-studio-gallery">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Photo gallery · {list.length}/{STUDIO_GALLERY_MAX}</Label>
        <div className="text-[10px] text-muted-foreground">Shown as a slideshow on the widget's space picker.</div>
      </div>
      {list.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {list.map((url, idx) => (
            <div key={idx} className="relative group border border-border" data-testid={`sb-studio-gallery-item-${idx}`}>
              <img src={_abs(url)} alt="" className="w-full h-24 object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                         className="text-white text-xs px-2 py-0.5 disabled:opacity-30 hover:bg-white/10"
                         data-testid={`sb-studio-gallery-up-${idx}`}>←</button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === list.length - 1}
                         className="text-white text-xs px-2 py-0.5 disabled:opacity-30 hover:bg-white/10"
                         data-testid={`sb-studio-gallery-down-${idx}`}>→</button>
                <button type="button" onClick={() => remove(idx)}
                         className="text-white text-xs px-2 py-0.5 hover:bg-red-500/70"
                         data-testid={`sb-studio-gallery-remove-${idx}`}>Remove</button>
              </div>
              <div className="absolute top-1 left-1 bg-black/50 text-white text-[9px] px-1 font-mono">{idx + 1}</div>
            </div>
          ))}
        </div>
      )}
      {list.length < STUDIO_GALLERY_MAX && (
        <ImageUpload key={uploaderKey} ws={ws} purpose="gallery"
                     value=""
                     onChange={add}
                     testid="sb-studio-gallery-uploader" />
      )}
    </div>
  );
}



// Feb 2026 · R268qqq · Compact PDF/DOCX/image uploader for studio
// guidelines. Reuses the studio-guidelines endpoint. Shows filename +
// clear button when a file is already set.
export function GuidelinesUpload({ ws, url, name, onChange }) {
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(
        `/workspaces/${ws}/studio/upload-guidelines`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange && onChange(data.url, data.filename || file.name);
      toast.success("Guidelines uploaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); }
  };
  const absUrl = url?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${url}`
    : (url || "");
  // R268xxx · Feb 2026 · Detect file kind so we can pick the right
  // preview embed — PDFs get an iframe, images render inline.
  const lower = (name || url || "").toLowerCase();
  const isPdf = lower.endsWith(".pdf");
  const isImage = /\.(png|jpe?g|gif|webp)$/i.test(lower);
  return (
    <div data-testid="sb-studio-guidelines">
      <Label className="text-xs">Studio guidelines file (PDF / DOC / image · attached to confirmation email)</Label>
      <div className="border border-border bg-muted/10 p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          {url ? (
            <div className="flex items-center gap-2">
              <span className="text-lg">📄</span>
              <a href={absUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline truncate"
                  data-testid="sb-studio-guidelines-link">{name || "guidelines file"}</a>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">No file uploaded yet.</div>
          )}
        </div>
        {url && (isPdf || isImage) && (
          <button type="button" onClick={() => setPreviewOpen(true)}
                   className="text-[11px] px-2 py-1 border border-border hover:border-primary hover:text-primary transition"
                   data-testid="sb-studio-guidelines-preview">
            👁 Preview
          </button>
        )}
        <label className="text-[11px] px-2 py-1 border border-primary text-primary cursor-pointer">
          {busy ? "Uploading…" : (url ? "Replace" : "Upload")}
          <input type="file" className="hidden"
                  accept=".pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  disabled={busy}
                  onChange={(e) => handle(e.target.files?.[0])}
                  data-testid="sb-studio-guidelines-input" />
        </label>
        {url && (
          <button type="button" onClick={() => onChange("", "")}
                   className="text-[11px] text-rose-600 hover:underline"
                   data-testid="sb-studio-guidelines-clear">Remove</button>
        )}
      </div>
      {/* Inline preview dialog — same file the customer sees on the
          booking confirmation screen. Admin can eyeball it before
          save so they catch typos, wrong branding, outdated maps. */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl h-[80vh] rounded-none flex flex-col p-0"
                        data-testid="sb-studio-guidelines-preview-dialog">
          <DialogHeader className="p-4 border-b border-border">
            <DialogTitle className="text-sm">Studio guidelines preview — {name || "guidelines file"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-muted/20">
            {isPdf && (
              <iframe src={absUrl} title="Guidelines preview"
                       className="w-full h-full border-0"
                       data-testid="sb-studio-guidelines-preview-iframe" />
            )}
            {isImage && (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={absUrl} alt="Guidelines"
                      className="max-w-full max-h-full object-contain"
                      data-testid="sb-studio-guidelines-preview-image" />
              </div>
            )}
          </div>
          <div className="p-3 border-t border-border flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Customers see this attached to the "Booking confirmed" email and via a download link on the confirmation screen.</span>
            <a href={absUrl} download={name || "guidelines"} target="_blank" rel="noopener noreferrer"
                className="text-primary hover:underline">Open in new tab ↗</a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export function ImageUpload({ ws, purpose, value, onChange, testid, disabled, aspect, cropMode, zoomMode }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [rawFile, setRawFile] = useState(null); // pending File awaiting crop
  const absUrl = value?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${value}`
    : (value || "");
  const uploadBlob = async (blob, filename) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", blob, filename || "image.jpg");
      const { data } = await api.post(
        `/workspaces/${ws}/studio/upload-image?purpose=${encodeURIComponent(purpose || "studio_cover")}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange && onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); }
  };
  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp|gif)/i.test(file.type)) {
      toast.error("Please upload a JPG, PNG, WebP or GIF"); return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB"); return;
    }
    // Feb 2026 · zoomMode replaces the forced crop editor. Any aspect
    // image can be uploaded — the user zooms and drags to frame it.
    if (zoomMode || cropMode) {
      setRawFile(file);
    } else {
      uploadBlob(file, file.name);
    }
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };
  return (
    <div data-testid={testid}>
      <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)} onDrop={onDrop}
            className={`border-2 border-dashed ${dragOver ? "border-primary bg-primary/5" : "border-border"} ${disabled ? "opacity-60" : ""} p-3 flex items-center gap-3`}>
        {absUrl ? (
          <img src={absUrl} alt="preview" className="w-20 h-14 object-cover border border-border" />
        ) : (
          <div className="w-20 h-14 border border-dashed border-border flex items-center justify-center bg-muted/20 text-[10px] text-muted-foreground uppercase">
            No image
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">Drop an image here, or</div>
          <div className="flex items-center gap-2 mt-1">
            <label className={`text-[11px] px-2 py-1 border border-primary text-primary cursor-pointer inline-block ${disabled ? "pointer-events-none" : ""}`}>
              {busy ? "Uploading…" : "Choose file"}
              <input type="file" accept="image/*" className="hidden"
                      disabled={disabled || busy}
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      data-testid={`${testid}-input`} />
            </label>
            {absUrl && (
              <button type="button" onClick={() => onChange("")}
                       disabled={disabled} data-testid={`${testid}-clear`}
                       className="text-[11px] text-rose-600 hover:underline">Remove</button>
            )}
          </div>
          {absUrl && <div className="text-[10px] text-muted-foreground truncate mt-1 font-mono">{value}</div>}
        </div>
      </div>
      {zoomMode ? (
        <ZoomPanDialog file={rawFile} aspect={aspect || 16 / 9}
                        onCancel={() => setRawFile(null)}
                        onDone={(blob) => { setRawFile(null); uploadBlob(blob, "framed.jpg"); }} />
      ) : (
        <CropDialog file={rawFile} aspect={aspect || 16 / 9}
                     onCancel={() => setRawFile(null)}
                     onDone={(blob) => { setRawFile(null); uploadBlob(blob, "cropped.jpg"); }} />
      )}
    </div>
  );
}

/* ─── Feb 2026 · Zoom-Pan image framer ────────────────────────────
   The classic "profile picture" editor: the image is placed inside a
   fixed-aspect viewport and the user pans + zooms it. No forced
   crop rectangle. On confirm we render to a canvas at the output
   resolution so the on-file image matches what the user framed.  */
export function ZoomPanDialog({ file, aspect, onCancel, onDone }) {
  const [src, setSrc] = useState(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const targetOutW = 1600;
  const targetOutH = Math.round(targetOutW / aspect);
  // Fit image inside stage using cover — the base scale that fills the
  // fixed-aspect viewport with the image.
  const stageMaxW = 640;
  const stageAspect = aspect;
  const stageW = stageMaxW;
  const stageH = Math.round(stageW / stageAspect);
  const cover = natural.w && natural.h
    ? Math.max(stageW / natural.w, stageH / natural.h)
    : 1;
  const drawScale = cover * zoom;
  const imgW = natural.w * drawScale;
  const imgH = natural.h * drawScale;
  // Constrain pan so image edges stay inside the viewport.
  const clamp = (p) => ({
    x: Math.min(0, Math.max(stageW - imgW, p.x)),
    y: Math.min(0, Math.max(stageH - imgH, p.y)),
  });
  useEffect(() => {
    if (!file) {
      setSrc(null); setNatural({ w: 0, h: 0 });
      setZoom(1); setPos({ x: 0, y: 0 });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSrc(reader.result);
    reader.readAsDataURL(file);
  }, [file]);
  const onImgLoad = (e) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    setNatural({ w, h });
    // Center the image
    const cov = Math.max(stageW / w, stageH / h);
    setPos({ x: (stageW - w * cov) / 2, y: (stageH - h * cov) / 2 });
    setZoom(1);
  };
  useEffect(() => {
    // Re-center when zoom changes: keep the center pixel stable
    if (!natural.w) return;
    setPos((prev) => {
      const cx = stageW / 2, cy = stageH / 2;
      const dx = (cx - prev.x) / (natural.w * drawScale);
      const dy = (cy - prev.y) / (natural.h * drawScale);
      // Just re-clamp for now — keep original centering unless dragged.
      return clamp(prev);
    });
    // eslint-disable-next-line
  }, [zoom, natural.w]);
  const onDown = (e) => {
    setDragging(true);
    const point = e.touches ? e.touches[0] : e;
    dragStart.current = { x: point.clientX, y: point.clientY, ox: pos.x, oy: pos.y };
  };
  const onMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - dragStart.current.x;
    const dy = point.clientY - dragStart.current.y;
    setPos(clamp({ x: dragStart.current.ox + dx, y: dragStart.current.oy + dy }));
  };
  const onUp = () => setDragging(false);
  const onWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.min(4, Math.max(1, z * (e.deltaY > 0 ? 0.92 : 1.08))));
  };
  const doExport = () => {
    if (!imgRef.current || !natural.w) return;
    // Map viewport → source pixels:
    // viewport pixel (vx, vy) shows source pixel ((vx - pos.x)/drawScale, (vy - pos.y)/drawScale)
    const canvas = document.createElement("canvas");
    canvas.width = targetOutW;
    canvas.height = targetOutH;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, targetOutW, targetOutH);
    const outScale = targetOutW / stageW;
    // The image occupies (pos.x, pos.y, imgW, imgH) on the stage.
    // Draw source image to (pos.x*outScale, pos.y*outScale, imgW*outScale, imgH*outScale).
    ctx.drawImage(
      imgRef.current,
      pos.x * outScale,
      pos.y * outScale,
      imgW * outScale,
      imgH * outScale,
    );
    canvas.toBlob((blob) => blob && onDone(blob), "image/jpeg", 0.92);
  };
  if (!file) return null;
  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="rounded-none max-w-3xl" data-testid="sb-zoom-dialog">
        <DialogHeader>
          <DialogTitle>Frame image · zoom &amp; drag to adjust</DialogTitle>
          <DialogDescription>
            Upload any aspect ratio. Zoom in/out and drag inside the preview to frame the shot.
          </DialogDescription>
        </DialogHeader>
        {src && (
          <div className="space-y-3">
            <div
              ref={stageRef}
              onMouseDown={onDown} onMouseMove={onMove}
              onMouseUp={onUp} onMouseLeave={onUp}
              onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
              onWheel={onWheel}
              style={{
                width: stageW,
                height: stageH,
                maxWidth: "100%",
                margin: "0 auto",
                position: "relative",
                overflow: "hidden",
                background: "#111",
                cursor: dragging ? "grabbing" : "grab",
                userSelect: "none",
                border: "1px solid #ccc",
                touchAction: "none",
              }}
              data-testid="sb-zoom-stage">
              <img ref={imgRef} src={src} alt="frame" onLoad={onImgLoad}
                    draggable={false}
                    style={{
                      position: "absolute",
                      left: pos.x,
                      top: pos.y,
                      width: imgW,
                      height: imgH,
                      maxWidth: "none",
                      pointerEvents: "none",
                    }} />
            </div>
            <div className="flex items-center gap-3 max-w-[640px] mx-auto">
              <span className="text-xs text-muted-foreground">Zoom</span>
              <input type="range" min="1" max="4" step="0.05"
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="flex-1"
                      data-testid="sb-zoom-slider" />
              <span className="text-xs font-mono w-12 text-right">{zoom.toFixed(2)}×</span>
            </div>
            <div className="text-[10px] text-muted-foreground text-center">
              Output {targetOutW} × {targetOutH}px · drag inside the preview to reposition
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none" onClick={doExport} data-testid="sb-zoom-apply">
            Use this frame
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function CropDialog({ file, aspect, onCancel, onDone }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  useEffect(() => {
    if (!file) { setSrc(null); setCrop(null); return; }
    const reader = new FileReader();
    reader.onload = () => setSrc(reader.result);
    reader.readAsDataURL(file);
  }, [file]);
  const onImageLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    // Center-fit an initial crop that satisfies the aspect ratio
    const targetH = w / aspect;
    let cropH = targetH;
    let cropW = w;
    if (targetH > h) { cropH = h; cropW = h * aspect; }
    setCrop({ unit: "px", x: (w - cropW) / 2, y: (h - cropH) / 2,
              width: cropW, height: cropH });
  };
  const doExport = async () => {
    const img = imgRef.current;
    const c = completedCrop || crop;
    if (!img || !c) return;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const cw = Math.round(c.width * scaleX);
    const ch = Math.round(c.height * scaleY);
    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, c.x * scaleX, c.y * scaleY, cw, ch, 0, 0, cw, ch);
    canvas.toBlob((blob) => blob && onDone(blob), "image/jpeg", 0.92);
  };
  if (!file) return null;
  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="rounded-none max-w-3xl" data-testid="sb-crop-dialog">
        <DialogHeader>
          <DialogTitle>Crop image · aspect {aspect.toFixed(2)}:1</DialogTitle>
        </DialogHeader>
        {src && (
          <>
            <style>{_CROP_INLINE_CSS}</style>
            <ReactCrop crop={crop} onChange={setCrop} onComplete={setCompletedCrop} aspect={aspect}>
              <img ref={imgRef} src={src} alt="to crop" onLoad={onImageLoad}
                    style={{ maxHeight: "60vh", maxWidth: "100%" }} />
            </ReactCrop>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none" onClick={doExport} data-testid="sb-crop-apply">
            Crop & upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MediaBadge({ value }) {
  const arr = Array.isArray(value) ? value : [];
  if (arr.length === 0) return <span className="text-muted-foreground">—</span>;
  const first = arr[0];
  const _abs = (u) => (u && u.startsWith("/api/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  return (
    <div className="flex items-center gap-1.5">
      {first.kind === "video" ? (
        <div className="w-10 h-7 bg-neutral-800 flex items-center justify-center text-white text-[8px]">▶</div>
      ) : (
        <img src={_abs(first.url)} alt="" className="w-10 h-7 object-cover border border-border" loading="lazy" />
      )}
      {arr.length > 1 && <span className="text-[10px] font-mono text-primary">+{arr.length - 1}</span>}
    </div>
  );
}
